if (!customElements.get("custom-product-as-variant")) {
  class CustomProductAsVariant extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("click", this.onVariantClick.bind(this));
    }

    onVariantClick(event) {
      event.preventDefault();

      // =========================
      // PRODUCT CARD CONTEXT (PLP, SEARCH, HOME, PDP "YOU MAY ALSO LIKE", ETC.)
      // =========================
      const productCard = this.closest(".product-card");
      if (productCard) {
        // Sync radio checked state within this specific product card
        const radioInput = this.querySelector('input[type="radio"]');
        if (radioInput) {
          const groupName = radioInput.name;
          productCard
            .querySelectorAll(`input[name="${CSS.escape(groupName)}"]`)
            .forEach(r => (r.checked = false));
          radioInput.checked = true;
        }

        const links         = productCard.querySelectorAll("a.product-card__media, .product-card__figure a, .product-title");
        const titleEl       = productCard.querySelector(".product-title");
        const primaryImg    = productCard.querySelector(".product-card__image--primary, .product-card__figure img:first-of-type, .product-card__media img:first-of-type");
        const secondaryImg  = productCard.querySelector(".product-card__image--secondary, .product-card__figure img.product-card__image--secondary, .product-card__media img.product-card__image--secondary");
        const quickBuyModal = productCard.querySelector("quick-buy-modal");

        // PRIMARY IMAGE
        if (primaryImg && this.dataset.image) {
          primaryImg.src = this.dataset.image;
          primaryImg.removeAttribute("srcset");
          if (this.dataset.alt) primaryImg.alt = this.dataset.alt;
        }

        // SECONDARY IMAGE
        if (secondaryImg && this.dataset.secondaryImage) {
          secondaryImg.src = this.dataset.secondaryImage;
          secondaryImg.removeAttribute("srcset");
          if (this.dataset.secondaryAlt) secondaryImg.alt = this.dataset.secondaryAlt;
        }

        // QUICK-BUY HANDLE
        if (quickBuyModal && this.dataset.variantUrl) {
          const baseHandle = this.dataset.variantUrl.replace("/products/", "").split("?")[0];
          quickBuyModal.setAttribute("handle", baseHandle);
        }

        // UPDATE LINKS (Figure image links, title link, etc.)
        if (this.dataset.variantUrl) {
          links.forEach(link => {
            link.href = this.dataset.variantUrl;
          });
        }

        // UPDATE TITLE
        if (titleEl && this.dataset.title) {
          titleEl.textContent = this.dataset.title;
        }

        // ACTIVE STATE (thumbnail-swatch / image mode)
        productCard
          .querySelectorAll(".thumbnail-swatch")
          .forEach(el => el.classList.remove("active"));

        this.classList.add("active");

        // UPDATE FORM VARIANT ID FOR DIRECT ADD TO CART
        if (this.dataset.variantId) {
          const variantInputs = productCard.querySelectorAll('input[name="id"]');
          variantInputs.forEach(inp => {
            inp.value = this.dataset.variantId;
          });
        }

        // UPDATE PRICES
        const isV2 = productCard.classList.contains("product-card--v2");
        if (this.dataset.price) {
          const salePriceEl = productCard.querySelector("[data-card-sale-price]");
          if (salePriceEl) {
            Array.from(salePriceEl.childNodes).forEach(node => {
              if (node.nodeType === Node.TEXT_NODE) node.remove();
            });
            salePriceEl.appendChild(document.createTextNode(this.dataset.price));
          }
        }

        if (this.dataset.compareAtPrice !== undefined) {
          const comparePriceEl = productCard.querySelector("[data-card-compare-price]");
          if (comparePriceEl) {
            Array.from(comparePriceEl.childNodes).forEach(node => {
              if (node.nodeType === Node.TEXT_NODE) node.remove();
            });
            const disc = parseInt(this.dataset.discount, 10) || 0;
            if (this.dataset.compareAtPrice && disc > 0) {
              comparePriceEl.appendChild(document.createTextNode(this.dataset.compareAtPrice));
              comparePriceEl.removeAttribute("hidden");
            } else {
              comparePriceEl.setAttribute("hidden", "");
            }
          }
        }

        if (this.dataset.discount !== undefined) {
          const badgeEl = productCard.querySelector("[data-card-discount-badge]");
          if (badgeEl) {
            const disc = parseInt(this.dataset.discount, 10) || 0;
            if (disc > 0) {
              badgeEl.textContent = isV2 ? (disc + "% OFF") : ("(" + disc + "% off)");
              badgeEl.removeAttribute("hidden");
            } else {
              badgeEl.textContent = "";
              badgeEl.setAttribute("hidden", "");
            }
          }
        }

        // UPDATE ATC BUTTON STATE
        const atcBtn = productCard.querySelector(".product-card__v2-atc-btn");
        if (atcBtn) {
          const atcText = atcBtn.querySelector(".product-card__v2-atc-text");
          const isAvailable = this.dataset.available !== "false";
          if (!isAvailable) {
            atcBtn.disabled = true;
            atcBtn.setAttribute("aria-disabled", "true");
            if (atcText) atcText.textContent = atcBtn.dataset.soldOutText || "Sold Out";
          } else {
            atcBtn.disabled = false;
            atcBtn.removeAttribute("aria-disabled");
            if (atcText) atcText.textContent = atcBtn.dataset.addToCartText || "Add to Cart";
          }
        }

        return;
      }

      // =========================
      // QUICK-BUY MODAL CONTEXT
      // =========================
      const isProductQuickBuy = this.closest("quick-buy-modal");
      if (isProductQuickBuy) {
        if (this.dataset.variantUrl) {
          window.location.href = this.dataset.variantUrl;
        }
        return;
      }

      // =========================
      // MAIN PRODUCT PAGE (PDP) CONTEXT
      // =========================
      const productInfo = this.closest("safe-sticky.product-info, .product-info");
      const isProductPage = productInfo || document.querySelector("safe-sticky.product-info");

      if (isProductPage) {
        // Since event.preventDefault() blocks the native label→radio association,
        // manually sync the radio checked state for color-swatch mode on main product.
        const radioInput = this.querySelector('input[type="radio"]');
        if (radioInput && !radioInput.checked) {
          const groupName = radioInput.name;
          document
            .querySelectorAll(`input[name="${CSS.escape(groupName)}"]`)
            .forEach(r => (r.checked = false));
          radioInput.checked = true;
        }

        const main = document.querySelector("#main");

        // Selectors for inner sections to transition
        const gallerySelectors = [
          ".product-gallery",
          ".product__gallery",
          ".product-gallery-list",
          ".product-page__gallery",
          ".product__media",
          ".media-gallery",
          "[class*='product-gallery']",
        ];

        const productInfoEl = document.querySelector("safe-sticky.product-info");

        let galleryEl = null;
        for (const sel of gallerySelectors) {
          galleryEl = document.querySelector(sel);
          if (galleryEl) break;
        }

        // ── Step 1: fade out INNER sections (not #main) ──────────────────────
        const fadeOut = (el) => {
          if (!el) return;
          el.style.transition    = "opacity 0.15s ease-out";
          el.style.opacity       = "0";
          el.style.pointerEvents = "none";
        };
        fadeOut(productInfoEl);
        fadeOut(galleryEl);

        // ── Step 2: fetch + wait for fade (in parallel) ───────────────────────
        const fetchDone = fetch(this.dataset.variantUrl).then(r => r.text());
        const fadeDone  = new Promise(resolve => setTimeout(resolve, 160));

        // ── Step 3: swap #main while inner sections are invisible ─────────────
        Promise.all([fetchDone, fadeDone])
          .then(([htmlText]) => {
            const html    = new DOMParser().parseFromString(htmlText, "text/html");
            const newMain = html.querySelector("#main");

            if (main && newMain) {
              main.innerHTML = newMain.innerHTML;   // ← original logic preserved

              if (typeof window.initVideoSlideElementsVisibility === "function") {
                window.initVideoSlideElementsVisibility();
              }
              document.dispatchEvent(new CustomEvent("variant:change", { bubbles: true }));
              document.dispatchEvent(new CustomEvent("shopify:section:load", { bubbles: true }));
            }

            // ✅ IMPORTANT: update URL
            window.history.pushState({}, "", this.dataset.variantUrl);

            // Re-query new elements (old references were destroyed by innerHTML swap)
            const newProductInfoEl = document.querySelector("safe-sticky.product-info");
            let newGalleryEl = null;
            for (const sel of gallerySelectors) {
              newGalleryEl = document.querySelector(sel);
              if (newGalleryEl) break;
            }

            // Set new elements to opacity:0 synchronously, BEFORE the browser
            // paints — so they never flash at opacity:1 after the swap.
            const newTargets = [newProductInfoEl, newGalleryEl].filter(Boolean);
            newTargets.forEach(el => {
              el.style.transition    = "none";
              el.style.opacity       = "0";
              el.style.pointerEvents = "none";
            });

            // ── Step 4: fade back in ────────────────────────────────────────
            // Double rAF ensures the browser has committed the opacity:0 paint
            // before we start the transition, so fade-in is actually visible.
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                newTargets.forEach(el => {
                  el.style.transition    = "opacity 0.2s ease-in";
                  el.style.opacity       = "1";
                  el.style.pointerEvents = "";
                });
                // Clean up inline styles after transition completes
                setTimeout(() => {
                  newTargets.forEach(el => {
                    el.style.transition = "";
                  });
                  if (typeof window.initVideoSlideElementsVisibility === "function") {
                    window.initVideoSlideElementsVisibility();
                  }
                }, 250);
              });
            });
          })
          .catch(() => {
            // On network error: hard-navigate as fallback
            window.location.href = this.dataset.variantUrl;
          });

        return;
      }
    }

    connectedCallback() {
      const cardV2 = this.closest('.product-card--v2');
      if (!cardV2) return;
      const container = this.closest('.variant-container, .pav-swatch-container, .product-card__v2-swatch-list');
      if (container) {
        requestAnimationFrame(() => autoFitCardSwatches(container));
      }
    }
  }

  customElements.define("custom-product-as-variant", CustomProductAsVariant);
}

// ============================================================================
// AUTOMATIC CARD SWATCH AUTO-FIT (Single-row dynamic responsive capacity)
// Fits 4 swatches (or more) when card width allows (e.g. Related Products),
// or gracefully shows 3 + remaining count when space is tighter (e.g. Homepage).
// Prevents wrapping to a second line.
// Strictly applies ONLY to .product-card--v2 (native product cards are untouched).
// ============================================================================
function autoFitCardSwatches(container) {
  if (!container) return;
  const card = container.closest('.product-card--v2');
  if (!card) return;

  const items = Array.from(container.children).filter(el => 
    el.tagName === 'CUSTOM-PRODUCT-AS-VARIANT' || 
    el.classList.contains('product-card__v2-swatch') ||
    el.classList.contains('thumbnail-swatch') ||
    el.classList.contains('pav-color-swatch-wrapper')
  );

  const plusBtn = container.querySelector('.swatch-plus-count');
  if (!items.length) return;

  const total = items.length;
  if (total <= 1) {
    if (plusBtn) plusBtn.style.display = 'none';
    return;
  }

  const containerWidth = container.clientWidth;
  if (containerWidth <= 0) return;

  const style = window.getComputedStyle(container);
  const gap = parseFloat(style.columnGap || style.gap) || 8;

  // Find sample item width
  let itemWidth = 0;
  for (const item of items) {
    const w = item.getBoundingClientRect().width || item.offsetWidth;
    if (w > 0) {
      itemWidth = w;
      break;
    }
  }
  if (!itemWidth) itemWidth = 40;

  // Plus button width
  let plusBtnWidth = 28;
  if (plusBtn) {
    const pw = plusBtn.getBoundingClientRect().width || plusBtn.offsetWidth;
    if (pw > 0) plusBtnWidth = pw;
  }

  let visibleCount = total;
  const allFitWidth = total * itemWidth + (total - 1) * gap;

  if (allFitWidth <= containerWidth + 2) {
    visibleCount = total;
  } else {
    const maxK = Math.floor((containerWidth - plusBtnWidth + gap) / (itemWidth + gap));
    visibleCount = Math.max(1, Math.min(total - 1, maxK));
  }

  items.forEach((item, idx) => {
    if (idx < visibleCount) {
      item.style.display = '';
    } else {
      item.style.display = 'none';
    }
  });

  const remaining = total - visibleCount;
  if (plusBtn) {
    if (remaining > 0) {
      plusBtn.textContent = '+' + remaining;
      plusBtn.style.display = 'inline-flex';
    } else {
      plusBtn.style.display = 'none';
    }
  }
}

function initAllCardSwatches() {
  const containers = document.querySelectorAll(
    '.product-card--v2 .variant-container, .product-card--v2 .pav-swatch-container, .product-card--v2 .product-card__v2-swatch-list'
  );
  containers.forEach(container => {
    autoFitCardSwatches(container);
    if (window.ResizeObserver && !container._hasAutoFitObserver) {
      container._hasAutoFitObserver = true;
      const ro = new ResizeObserver(() => {
        autoFitCardSwatches(container);
      });
      ro.observe(container);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllCardSwatches);
} else {
  initAllCardSwatches();
}

window.addEventListener('resize', initAllCardSwatches);
document.addEventListener('shopify:section:load', initAllCardSwatches);
document.addEventListener('theme:loading:end', initAllCardSwatches);
document.addEventListener('variant:change', initAllCardSwatches);
